import React, { useState, useEffect, useMemo } from "react";
import axios from "../../../api/axios";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import "./DarDeAlta.css";

export default function DarDeAlta() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const [installation, setInstallation] = useState(null);
  const [onus, setOnus] = useState([]);
  const [selectedOnu, setSelectedOnu] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  // CONFIGURACIÓN DE CORTE CON HORA
  const [diaCorte, setDiaCorte] = useState("");
  const [horaCorte, setHoraCorte] = useState("00");
  const [minutoCorte, setMinutoCorte] = useState("00");
  const [toleranciaDias, setToleranciaDias] = useState(5);
  const [corteAutomatico, setCorteAutomatico] = useState(true);

  // CONFIGURACIÓN DE IP
  const [pools, setPools] = useState([]);
  const [selectedPool, setSelectedPool] = useState(null);
  const [selectedIp, setSelectedIp] = useState("");
  const [availableIps, setAvailableIps] = useState([]);
  const [loadingIps, setLoadingIps] = useState(false);

  // Generar opciones de horas (00-23)
  const horas = Array.from({ length: 24 }, (_, i) => ({
    value: i.toString().padStart(2, '0'),
    label: i.toString().padStart(2, '0') + ':00'
  }));

  // Generar opciones de minutos (00,15,30,45) para simplificar
  const minutos = [
    { value: '00', label: '00' },
    { value: '15', label: '15' },
    { value: '30', label: '30' },
    { value: '45', label: '45' }
  ];

  useEffect(() => {
    const fetchData = async () => {
      try {
        console.log("Cargando datos para instalación ID:", id);

        if (location.state?.installation) {
          console.log("Usando datos del state");
          setInstallation(location.state.installation);
        } else {
          console.log("Obteniendo instalación de la API");
          const res = await axios.get(`/installations/${id}`);
          setInstallation(res.data);
        }

        console.log("Obteniendo ONUs libres");
        const onusRes = await axios.get("/customers/onus-free");
        console.log(`Total ONUs libres: ${onusRes.data.length}`);
        setOnus(onusRes.data);
      } catch (err) {
        console.error("Error cargando datos:", err);
        alert(err.response?.data?.detail || "Error cargando datos");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id, location.state]);

  // Cargar pools cuando se selecciona una ONU (asociada a una OLT)
  useEffect(() => {
    console.log("useEffect - selectedOnu cambió:", selectedOnu);
    if (selectedOnu && selectedOnu.olt_id) {
      console.log("OLT ID encontrado:", selectedOnu.olt_id);
      loadPools();
    } else if (selectedOnu) {
      console.log("ONU seleccionada pero no tiene olt_id:", selectedOnu);
    }
  }, [selectedOnu]);

  const loadPools = async () => {
    console.log("Cargando pools para OLT ID:", selectedOnu.olt_id);
    try {
      const response = await axios.get(`/pools?olt_id=${selectedOnu.olt_id}`);
      console.log("Pools recibidos del backend:", response.data);
      setPools(response.data);
      if (response.data.length === 0) {
        console.log("No hay pools asociados a esta OLT");
      }
    } catch (error) {
      console.error('Error cargando pools:', error);
    }
  };

  const loadAvailableIps = async (poolId) => {
    console.log("Cargando IPs disponibles para pool ID:", poolId);
    setLoadingIps(true);
    try {
      const response = await axios.get(`/pools/${poolId}/free-ips`);
      console.log("IPs disponibles:", response.data);
      setAvailableIps(response.data);
    } catch (error) {
      console.error('Error cargando IPs disponibles:', error);
      setAvailableIps([]);
    } finally {
      setLoadingIps(false);
    }
  };

  // Filtrar ONUs por búsqueda
  const filteredOnus = useMemo(() => {
    if (!searchTerm.trim()) return onus;

    const term = searchTerm.toLowerCase().trim();

    return onus.filter(onu => {
      return (
        (onu.id?.toString().toLowerCase().includes(term)) ||
        (onu.onu_id?.toString().toLowerCase().includes(term)) ||
        (onu.name?.toLowerCase().includes(term)) ||
        (onu.olt_id?.toString().toLowerCase().includes(term)) ||
        (onu.pon?.toLowerCase().includes(term)) ||
        (onu.serial?.toLowerCase().includes(term))
      );
    });
  }, [onus, searchTerm]);

  const handleSelectOnu = (onuId) => {
    const onuSeleccionada = onus.find(onu => onu.id === parseInt(onuId));
    console.log("=== ONU SELECCIONADA ===");
    console.log("ONU completa:", onuSeleccionada);
    console.log("OLT ID de la ONU:", onuSeleccionada?.olt_id);
    console.log("¿Tiene OLT ID?", !!onuSeleccionada?.olt_id);
    setSelectedOnu(onuSeleccionada);
    // Resetear selección de IP al cambiar ONU
    setSelectedPool(null);
    setSelectedIp("");
    setAvailableIps([]);
  };

  const handleInstall = async () => {
    console.log("=== INICIANDO INSTALACIÓN ===");
    console.log("ID Instalación:", id);
    console.log("ONU seleccionada:", selectedOnu);
    console.log("Configuración de corte:", {
      diaCorte,
      horaCorte,
      minutoCorte,
      toleranciaDias,
      corteAutomatico
    });
    console.log("Configuración IP:", {
      poolId: selectedPool?.id,
      ip: selectedIp
    });

    if (!selectedOnu) {
      alert("Debe seleccionar una ONU");
      return;
    }

    if (!diaCorte) {
      alert("Debe seleccionar un día de corte");
      return;
    }

    setSubmitting(true);

    try {
      const payload = {
        onu_id: selectedOnu.id,
        dia_corte: parseInt(diaCorte),
        hora_corte: parseInt(horaCorte),
        minuto_corte: parseInt(minutoCorte),
        tolerancia_dias: toleranciaDias,
        corte_automatico: corteAutomatico,
        // Campos de IP (solo si se seleccionó pool e IP)
        pool_id: selectedPool?.id || null,
        ip_asignada: selectedIp || null
      };

      console.log("Enviando petición POST a:", `/installations/${id}/install`);
      console.log("Payload completo:", payload);

      const response = await axios.post(`/installations/${id}/install`, payload);

      console.log("Respuesta del servidor:", response.data);
      alert("✅ Instalación completada correctamente");
      navigate("/clientes/instalaciones");

    } catch (err) {
      console.error("❌ Error en la instalación:");
      console.error("Mensaje:", err.message);
      console.error("Respuesta del servidor:", err.response?.data);
      console.error("Estado HTTP:", err.response?.status);

      let errorMessage = "Error al instalar";

      if (err.response?.status === 400) {
        errorMessage = err.response?.data?.detail || "La instalación no está aprobada o no tiene técnico asignado";
      } else if (err.response?.status === 401) {
        errorMessage = "No autorizado. Por favor inicie sesión nuevamente";
      } else if (err.response?.status === 404) {
        errorMessage = "Instalación u ONU no encontrada";
      } else {
        errorMessage = err.response?.data?.detail || err.message;
      }

      alert(`❌ ${errorMessage}`);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="instalacion-loading-container">
        <div className="instalacion-loading-spinner"></div>
        <p>Cargando datos de instalación...</p>
      </div>
    );
  }

  if (!installation) {
    return (
      <div className="instalacion-error-container">
        <div className="instalacion-error-card">
          <div className="error-icon">❌</div>
          <h3>Instalación no encontrada</h3>
          <p>La instalación que buscas no existe</p>
          <button
            onClick={() => navigate("/clientes/instalaciones")}
            className="instalacion-btn-primary"
          >
            Volver a Instalaciones
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="instalacion-container">
      {/* Header */}
      <div className="instalacion-header">
        <div className="instalacion-header-left">
          <div className="instalacion-header-icon">🔧</div>
          <div>
            <h1>Finalizar Instalación #{id}</h1>
            <p>Complete los datos para finalizar el proceso de instalación</p>
          </div>
        </div>
      </div>

      {/* Datos del Cliente */}
      <div className="cliente-card">
        <div className="cliente-card-header">
          <span className="card-icon">👤</span>
          <h2>Datos del Cliente</h2>
        </div>
        <div className="cliente-card-body">
          <div className="cliente-grid">
            <div className="cliente-info-item">
              <span className="info-label">Cliente</span>
              <span className="info-value">{installation.nombres} {installation.apellidos}</span>
            </div>
            <div className="cliente-info-item">
              <span className="info-label">Identificación</span>
              <span className="info-value">{installation.identificacion}</span>
            </div>
            <div className="cliente-info-item">
              <span className="info-label">Plan</span>
              <span className="info-value">{installation.plan}</span>
            </div>
            <div className="cliente-info-item">
              <span className="info-label">Estado</span>
              <span className={`estado-badge estado-${installation.estado?.toLowerCase() || 'pendiente'}`}>
                {installation.estado || 'PENDIENTE'}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Configuración de Corte */}
      <div className="corte-card">
        <div className="corte-card-header">
          <span className="card-icon">⏰</span>
          <h2>Configuración de Corte</h2>
        </div>
        <div className="corte-card-body">
          <div className="corte-grid">
            {/* Día de corte */}
            <div className="corte-field">
              <label className="corte-label required">
                Día del mes <span className="required-star">*</span>
              </label>
              <select
                value={diaCorte}
                onChange={(e) => setDiaCorte(e.target.value)}
                className="corte-select"
                required
              >
                <option value="">Seleccione día</option>
                {[...Array(31)].map((_, i) => (
                  <option key={i+1} value={i+1}>{i+1}</option>
                ))}
              </select>
            </div>

            {/* Hora de corte */}
            <div className="corte-field">
              <label className="corte-label">Hora</label>
              <select
                value={horaCorte}
                onChange={(e) => setHoraCorte(e.target.value)}
                className="corte-select"
              >
                {horas.map(h => (
                  <option key={h.value} value={h.value}>{h.label}</option>
                ))}
              </select>
            </div>

            {/* Minuto de corte */}
            <div className="corte-field">
              <label className="corte-label">Minuto</label>
              <select
                value={minutoCorte}
                onChange={(e) => setMinutoCorte(e.target.value)}
                className="corte-select"
              >
                {minutos.map(m => (
                  <option key={m.value} value={m.value}>{m.label}</option>
                ))}
              </select>
            </div>

            {/* Días de gracia */}
            <div className="corte-field">
              <label className="corte-label">Días de gracia</label>
              <input
                type="number"
                value={toleranciaDias}
                onChange={(e) => setToleranciaDias(parseInt(e.target.value) || 0)}
                min="0"
                max="30"
                className="corte-input"
              />
            </div>
          </div>

          {/* Corte automático */}
          <div className="corte-automatico">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={corteAutomatico}
                onChange={(e) => setCorteAutomatico(e.target.checked)}
                className="checkbox-input"
              />
              <span className="checkbox-text">Activar corte automático</span>
            </label>
          </div>

          {/* Vista previa */}
          {diaCorte && (
            <div className="corte-preview">
              <span className="preview-icon">⏰</span>
              <p className="preview-text">
                <strong>Corte programado:</strong> Día {diaCorte} de cada mes a las {horaCorte}:{minutoCorte} horas
                {corteAutomatico && (
                  <span className="preview-gracia">
                    (Suspensión después de {toleranciaDias} días de gracia)
                  </span>
                )}
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Asignación de IP - Solo aparece si hay pools disponibles */}
      {selectedOnu && pools.length > 0 && (
        <div className="ip-card">
          <div className="ip-card-header">
            <span className="card-icon">🌐</span>
            <h2>Asignación de IP</h2>
            <small className="ip-card-subtitle">(Opcional)</small>
          </div>
          <div className="ip-card-body">
            <div className="ip-field">
              <label className="ip-label">Pool IP</label>
              <select
                value={selectedPool?.id || ""}
                onChange={(e) => {
                  const pool = pools.find(p => p.id === parseInt(e.target.value));
                  console.log("Pool seleccionado:", pool);
                  setSelectedPool(pool);
                  if (pool) loadAvailableIps(pool.id);
                  setSelectedIp("");
                }}
                className="ip-select"
              >
                <option value="">-- Sin asignación de IP --</option>
                {pools.map(pool => (
                  <option key={pool.id} value={pool.id}>
                    {pool.nombre} - {pool.network}/{pool.mascara}
                  </option>
                ))}
              </select>
            </div>

            {selectedPool && (
              <div className="ip-field">
                <label className="ip-label">IP a asignar</label>
                {loadingIps ? (
                  <div className="loading-ips">
                    <span className="loading-spinner-small"></span> Cargando IPs disponibles...
                  </div>
                ) : (
                  <select
                    value={selectedIp}
                    onChange={(e) => setSelectedIp(e.target.value)}
                    className="ip-select"
                  >
                    <option value="">Seleccione una IP</option>
                    {availableIps.map(ip => (
                      <option key={ip} value={ip}>{ip}</option>
                    ))}
                  </select>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Mensaje si no hay pools disponibles */}
      {selectedOnu && pools.length === 0 && (
        <div className="ip-card">
          <div className="ip-card-header">
            <span className="card-icon">⚠️</span>
            <h2>Asignación de IP</h2>
          </div>
          <div className="ip-card-body">
            <div className="loading-ips" style={{ backgroundColor: '#fef3c7', borderColor: '#fcd34d', color: '#92400e' }}>
              No hay pools IP disponibles para esta OLT. Crea un pool en la sección "Pools IP" de MikroTik.
            </div>
          </div>
        </div>
      )}

      {/* Selector de ONU */}
      <div className="onu-card">
        <div className="onu-card-header">
          <span className="card-icon">📡</span>
          <h2>Seleccionar ONU</h2>
        </div>
        <div className="onu-card-body">
          {/* Buscador */}
          <div className="onu-search">
            <input
              type="text"
              placeholder="Buscar ONU por ID, OLT, PON..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="onu-search-input"
            />
            <div className="onu-search-info">
              <span className="onu-count">Mostrando {filteredOnus.length} de {onus.length} ONUs</span>
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm("")}
                  className="onu-search-clear"
                >
                  Limpiar
                </button>
              )}
            </div>
          </div>

          {/* Selector con scroll */}
          <select
            className="onu-select"
            value={selectedOnu?.id || ""}
            onChange={(e) => handleSelectOnu(e.target.value)}
            size={8}
            disabled={submitting}
          >
            <option value="">-- Seleccione una ONU --</option>
            {filteredOnus.map((onu) => (
              <option key={onu.id} value={onu.id}>
                OLT {onu.olt_id} | PON {onu.pon} | ID: {onu.id} | {onu.name || ''}
              </option>
            ))}
          </select>

          {/* Detalles de ONU seleccionada */}
          {selectedOnu && (
            <div className="onu-seleccionada">
              <div className="seleccionada-icon">📡</div>
              <div className="seleccionada-info">
                <p className="seleccionada-titulo">ONU Seleccionada:</p>
                <p className="seleccionada-detalle">
                  OLT {selectedOnu.olt_id} | PON {selectedOnu.pon} | ID: {selectedOnu.id}
                  {selectedOnu.name && <span className="seleccionada-nombre"> | {selectedOnu.name}</span>}
                </p>
              </div>
            </div>
          )}

          {installation.estado !== 'APROBADA' && (
            <div className="onu-warning">
              <span className="warning-icon">⚠️</span>
              <p className="warning-text">
                Esta instalación debe estar APROBADA para continuar
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Botones */}
      <div className="instalacion-actions">
        <button
          onClick={() => navigate(-1)}
          disabled={submitting}
          className="btn-cancelar"
        >
          Cancelar
        </button>

        <button
          onClick={handleInstall}
          disabled={!selectedOnu || !diaCorte || submitting || installation.estado !== 'APROBADA'}
          className="btn-confirmar"
        >
          {submitting ? (
            <>
              <span className="loading-spinner-small"></span>
              Instalando...
            </>
          ) : (
            '✅ Confirmar Instalación'
          )}
        </button>
      </div>
    </div>
  );
}
